import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AddPayComponent = ({ show, onHide, onComponentAdded }) => {
  const [formData, setFormData] = useState({
    component_name: '',
    component_type: 'Earning',
    calculation_type: 'Fixed',
    value: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [isLoadingComponents, setIsLoadingComponents] = useState(false);
  const [componentNameError, setComponentNameError] = useState('');

  const textareaRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE;

  // Validate component name as valid variable name
  const validateComponentName = (name) => {
    // Check if name is empty
    if (!name.trim()) {
      return 'Component Name is required';
    }

    // Check if first character is an alphabet
    const firstChar = name.charAt(0);
    if (!/[a-zA-Z]/.test(firstChar)) {
      return 'Component Name must start with a letter (A-Z or a-z)';
    }

    // Check if contains only alphanumeric and underscore characters
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
      return 'Component Name can only contain letters (A-Z, a-z), numbers (0-9), and underscores (_)';
    }

    // Check for spaces
    if (/\s/.test(name)) {
      return 'Component Name cannot contain spaces';
    }

    // Check length (optional, adjust as needed)
    if (name.length > 50) {
      return 'Component Name must be 50 characters or less';
    }

    return ''; // No error
  };

  // Fetch available pay components from the server (excluding CTC and Global components only)
  const fetchAvailableComponents = useCallback(async () => {
    try {
      setIsLoadingComponents(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_pay_components.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Filter active components, exclude only CTC and Global components from formula suggestions
        // General components are now ALLOWED in formulas
        const componentNames = response.data.components
          .filter(comp => comp.is_active && comp.component_type !== 'CTC' && comp.component_type !== 'Global')
          .map(comp => comp.component_name);
        setAvailableComponents(componentNames);
      }
    } catch (error) {
      console.error('Error fetching components:', error);
    } finally {
      setIsLoadingComponents(false);
    }
  }, [API_BASE]);

  // Fetch available components when calculation type is Formula
  useEffect(() => {
    if (show && formData.calculation_type === 'Formula') {
      fetchAvailableComponents();
    }
  }, [show, formData.calculation_type, fetchAvailableComponents]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'component_name') {
      // Validate component name in real-time
      const errorMessage = validateComponentName(value);
      setComponentNameError(errorMessage);

      // Only update if no error or user is still typing
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    } else if (name === 'component_type') {
      // When component type changes, adjust calculation type if needed
      const newComponentType = value;
      const newFormData = {
        ...formData,
        component_type: newComponentType
      };

      // If component type is 'Global', restrict calculation type to 'Fixed' or 'Percentage'
      if (newComponentType === 'Global') {
        if (formData.calculation_type === 'Formula') {
          newFormData.calculation_type = 'Fixed';
          newFormData.value = '';
        }
      }

      setFormData(newFormData);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFormulaChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      value: value
    }));

    // Update cursor position
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }

    // Show suggestions based on what user is typing
    if (value.length > 0) {
      const lastWord = getLastWord(value, cursorPosition);
      if (lastWord && lastWord.length > 0) {
        const filtered = availableComponents.filter(comp =>
          comp.toLowerCase().includes(lastWord.toLowerCase())
        );
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const getLastWord = (text, position) => {
    // Extract the word being typed at cursor position
    const textBeforeCursor = text.substring(0, position);
    const words = textBeforeCursor.split(/[\s+\-*/()]+/);
    return words[words.length - 1] || '';
  };

  const handleSuggestionClick = (suggestion) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const value = formData.value;
    const position = cursorPosition;

    // Find the start of the current word
    const textBeforeCursor = value.substring(0, position);
    const lastDelimiterIndex = Math.max(
      textBeforeCursor.lastIndexOf(' '),
      textBeforeCursor.lastIndexOf('+'),
      textBeforeCursor.lastIndexOf('-'),
      textBeforeCursor.lastIndexOf('*'),
      textBeforeCursor.lastIndexOf('/'),
      textBeforeCursor.lastIndexOf('('),
      textBeforeCursor.lastIndexOf(')'),
      -1
    );

    const newText =
      value.substring(0, lastDelimiterIndex + 1) +
      suggestion +
      value.substring(position);

    setFormData(prev => ({
      ...prev,
      value: newText
    }));

    setShowSuggestions(false);

    // Focus back to textarea and set cursor position
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newPosition = lastDelimiterIndex + 1 + suggestion.length;
        textarea.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }
    }, 0);
  };

  const handleTextareaClick = (e) => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Tab' && showSuggestions && suggestions.length > 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[0]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate component name before submitting
    const nameError = validateComponentName(formData.component_name);
    if (nameError) {
      setComponentNameError(nameError);
      return; // Prevent form submission
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/add_pay_component.php`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onComponentAdded();
        onHide();
        setFormData({
          component_name: '',
          component_type: 'Earning',
          calculation_type: 'Fixed',
          value: '',
          is_active: true
        });
        setComponentNameError('');
        setSuggestions([]);
        setShowSuggestions(false);
      } else {
        setError(response.data.error || 'Failed to add pay component');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add pay component');
      console.error('Error adding pay component:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderBottom: 'none'
          }}>
            <h5 className="modal-title text-white">
              <i className="bi bi-plus-circle me-2"></i>
              Add New Pay Component
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="componentName" className="form-label fw-medium">
                      Component Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control border-secondary-subtle ${componentNameError ? 'is-invalid' : ''}`}
                      id="componentName"
                      name="component_name"
                      value={formData.component_name}
                      onChange={handleChange}
                      placeholder="e.g., Basic_Salary, HRA, PF, Gross_Salary"
                      required
                    />
                    {componentNameError && (
                      <div className="invalid-feedback d-flex align-items-center">
                        <i className="bi bi-exclamation-circle-fill me-2"></i>
                        {componentNameError}
                      </div>
                    )}
                    <div className="form-text text-muted">
                      <div className="mb-1">Must follow variable naming rules:</div>
                      <ul className="small mb-0">
                        <li>Start with a letter (A-Z, a-z)</li>
                        <li>Can contain letters, numbers, and underscores (_)</li>
                        <li>No spaces or special characters</li>
                        <li>Examples: Basic_Salary, HRA, PF_Contribution, Gross_Salary_2024</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="componentType" className="form-label fw-medium">
                      Component Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select border-secondary-subtle"
                      id="componentType"
                      name="component_type"
                      value={formData.component_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="Earning">Earning</option>
                      <option value="Deduction">Deduction</option>
                      <option value="CTC">CTC</option>
                      <option value="General">General</option>
                      <option value="Global">Global</option>
                    </select>
                    <div className="form-text text-muted">
                      CTC, General, and Global components are typically total cost to company items.
                      {formData.component_type === 'Global' && (
                        <span className="text-primary fw-medium d-block mt-1">
                          <i className="bi bi-info-circle me-1"></i>
                          Global components can only use Fixed or Percentage calculation types.
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="calculationType" className="form-label fw-medium">
                      Calculation Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select border-secondary-subtle"
                      id="calculationType"
                      name="calculation_type"
                      value={formData.calculation_type}
                      onChange={handleChange}
                      required
                      disabled={formData.component_type === 'Global' && formData.calculation_type !== 'Fixed' && formData.calculation_type !== 'Percentage'}
                    >
                      <option value="Fixed">Fixed Amount</option>
                      <option value="Percentage">Percentage of Base</option>
                      <option value="Formula" disabled={formData.component_type === 'Global'}>Custom Formula</option>
                    </select>
                    <div className="form-text text-muted">
                      How this component should be calculated
                      {formData.component_type === 'Global' && (
                        <span className="text-primary fw-medium d-block mt-1">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Global components cannot use Formula calculation type.
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="value" className="form-label fw-medium">
                      {formData.calculation_type === 'Fixed' ? 'Amount' :
                        formData.calculation_type === 'Percentage' ? 'Percentage (%)' :
                          'Formula'} <span className="text-danger">*</span>
                    </label>
                    {formData.calculation_type === 'Formula' ? (
                      <div className="position-relative">
                        <textarea
                          ref={textareaRef}
                          className="form-control border-secondary-subtle"
                          id="value"
                          rows="3"
                          name="value"
                          value={formData.value}
                          onChange={handleFormulaChange}
                          onClick={handleTextareaClick}
                          onKeyDown={handleTextareaKeyDown}
                          placeholder="e.g., Basic_Salary + DA + HRA"
                          required
                        />
                        {isLoadingComponents && (
                          <div className="position-absolute top-0 end-0 mt-2 me-2">
                            <span className="spinner-border spinner-border-sm"
                              style={{ color: '#667eea' }}
                              role="status"
                            ></span>
                          </div>
                        )}

                        {showSuggestions && suggestions.length > 0 && (
                          <div className="dropdown-menu show w-100 shadow-sm" style={{ zIndex: 1060 }}>
                            <div className="dropdown-header small text-primary fw-medium">
                              <i className="bi bi-lightbulb me-1"></i>
                              Available Components (excluding CTC and Global components)
                            </div>
                            {suggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                className="dropdown-item py-2"
                                onClick={() => handleSuggestionClick(suggestion)}
                              >
                                <i className="bi bi-tag me-2" style={{ color: '#667eea' }}></i>
                                <span className="text-dark">{suggestion}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="form-control border-secondary-subtle"
                        id="value"
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        placeholder={formData.calculation_type === 'Fixed' ? 'e.g., 5000' : 'e.g., 10'}
                        required
                      />
                    )}
                    <div className="form-text text-muted">
                      {formData.calculation_type === 'Fixed' ? 'Enter fixed amount' :
                        formData.calculation_type === 'Percentage' ? 'Enter percentage value' :
                          <span>
                            Enter formula using component names. Use +, -, *, /, () for calculations.
                            <br />
                            <small className="text-primary fw-medium">
                              <i className="bi bi-info-circle me-1"></i>
                              CTC and Global components are not available for use in formulas. General components are allowed.
                            </small>
                          </span>}
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  {formData.calculation_type === 'Formula' && availableComponents.length > 0 && (
                    <div className="card mb-3 border-0 shadow-sm">
                      <div className="card-header" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        borderRadius: '0.375rem 0.375rem 0 0'
                      }}>
                        <h6 className="mb-0 text-white fw-medium">
                          <i className="bi bi-list-ul me-2"></i>
                          Available Components for Formula
                          <small className="text-white-50 ms-2 fw-normal">
                            (excluding CTC and Global components)
                          </small>
                        </h6>
                      </div>
                      <div className="card-body" style={{
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                      }}>
                        <div className="d-flex flex-wrap gap-2">
                          {availableComponents.map((component, index) => (
                            <span
                              key={index}
                              className="badge px-3 py-2"
                              style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }}
                              onClick={() => {
                                const textarea = textareaRef.current;
                                if (textarea) {
                                  const startPos = textarea.selectionStart;
                                  const endPos = textarea.selectionEnd;
                                  const newValue =
                                    formData.value.substring(0, startPos) +
                                    component +
                                    formData.value.substring(endPos);
                                  setFormData(prev => ({ ...prev, value: newValue }));

                                  setTimeout(() => {
                                    textarea.focus();
                                    const newPosition = startPos + component.length;
                                    textarea.setSelectionRange(newPosition, newPosition);
                                    setCursorPosition(newPosition);
                                  }, 0);
                                }
                              }}
                            >
                              <i className="bi bi-plus-circle me-1"></i>
                              {component}
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 text-muted small">
                          <i className="bi bi-info-circle me-1"></i>
                          Click on any component to insert it into the formula. CTC and Global components are excluded.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="active-switch"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />
                    <label className="form-check-label fw-medium" htmlFor="active-switch" style={{
                      color: formData.is_active ? '#198754' : '#6c757d'
                    }}>
                      {formData.is_active ? 'Active Component' : 'Inactive Component'}
                    </label>
                    <div className="form-text text-muted">
                      Inactive components won't be available for payroll calculations
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              borderTop: '1px solid rgba(102, 126, 234, 0.1)'
            }}>
              <button
                type="button"
                className="btn btn-light border-secondary-subtle"
                onClick={onHide}
                disabled={loading}
                style={{
                  background: 'white',
                  border: '1px solid #dee2e6',
                  color: '#6c757d',
                  transition: 'all 0.2s',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f8f9fa';
                  e.target.style.borderColor = '#adb5bd';
                  e.target.style.color = '#495057';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.color = '#6c757d';
                }}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white border-0 shadow-sm"
                disabled={loading || componentNameError}
                style={{
                  background: componentNameError ?
                    'linear-gradient(135deg, #6c757d 0%, #495057 100%)' :
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s',
                  padding: '0.5rem 1.5rem',
                  opacity: componentNameError ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!componentNameError) {
                    e.target.style.background = 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!componentNameError) {
                    e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 6px rgba(102, 126, 234, 0.2)';
                  }
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Component
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPayComponent;